/**
 * Іконки шапки (ч.962, запит власника):
 *   • «Аа» — масштаб шрифта (звичайний → більший → великий по колу);
 *   • сонце/місяць — режим день/ніч;
 *   • стрілка з дверей — вихід (замість текстової кнопки «Вийти»), стоїть
 *     ПІСЛЯ блоку облікового запису, скраю екрана (ч.963).
 *
 * Іконки — inline SVG з `currentColor` (самі перефарбовуються у нічній
 * темі); підпис лишається у `title`/`aria-label`, щоб кнопка була
 * зрозумілою і доступною з клавіатури.
 */
import { useSyncExternalStore } from 'react';

import {
  FONT_LABELS, effectiveTheme, getFont, getTheme, nextFont,
  type FontScale,
  setFont, setTheme, subscribeAppearance,
} from '@/store/appearance';

const ICON = { width: 17, height: 17, viewBox: '0 0 24 24', fill: 'none' } as const;

function SunIcon() {
  return (
    <svg {...ICON} stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden>
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...ICON} stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden>
      <path d="M20 13.5A8 8 0 0 1 10.5 4a8.2 8.2 0 0 0 0 16 8 8 0 0 0 9.5-6.5Z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg {...ICON} stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 4H5.5A1.5 1.5 0 0 0 4 5.5v13A1.5 1.5 0 0 0 5.5 20H9" />
      <path d="M16 8l4 4-4 4M20 12h-9" />
    </svg>
  );
}

/** «Аа»: велика і мала літери — впізнаваний знак розміру шрифта.
 *  ч.1023: чотири кроки (додано `sm`) — розмір «А» показує поточний. */
const _BIG: Record<FontScale, number> = { sm: 11, md: 13, lg: 15, xl: 17 };

function FontIcon({ scale }: { scale: FontScale }) {
  const big = _BIG[scale];
  return (
    <svg {...ICON} aria-hidden>
      <text x="1" y="17.5" fontSize={big}
            fontWeight={700} fill="currentColor">A</text>
      <text x={big} y="17.5"
            fontSize={scale === 'sm' ? 9 : 10} fontWeight={600}
            fill="currentColor">а</text>
    </svg>
  );
}

/** ч.963 (зауваження власника): «вийти» — ПІСЛЯ блоку облікового запису,
 *  скраю екрана; тому кнопка винесена в окремий експорт. */
export function HeaderLogoutButton({ onLogout }: { onLogout: () => void }) {
  return (
    <button type="button" className="app__iconbtn" onClick={onLogout}
      title="Вийти" aria-label="Вийти">
      <LogoutIcon />
    </button>
  );
}

export default function HeaderIconActions() {
  // Підписка на зміни оформлення (у т.ч. на зміну теми ОС у режимі `system`).
  useSyncExternalStore(subscribeAppearance, () => `${getTheme()}|${getFont()}`,
    () => 'system|md');

  const font = getFont();
  const dark = effectiveTheme() === 'dark';

  return (
    <>
      <button type="button" className="app__iconbtn"
        onClick={() => setFont(nextFont(font))}
        title={`Розмір шрифта: ${FONT_LABELS[font]} (натисніть, щоб змінити)`}
        aria-label={`Розмір шрифта: ${FONT_LABELS[font]}`}>
        <FontIcon scale={font} />
      </button>
      <button type="button" className="app__iconbtn"
        onClick={() => setTheme(dark ? 'light' : 'dark')}
        title={dark ? 'Нічний режим увімкнено — перемкнути на денний'
                    : 'Денний режим — перемкнути на нічний'}
        aria-label={dark ? 'Перемкнути на денний режим' : 'Перемкнути на нічний режим'}>
        {dark ? <MoonIcon /> : <SunIcon />}
      </button>
    </>
  );
}
